import { useValidation } from '@application/utils';
import { Analytics } from '@application/utils/analytics';
import { EmailContactCreatedFromEnum } from '@gql/graphql';
import type { PromoNewsletterData, PromoNewsletterFormValues } from '@presentation/components';
import type { FormikConfig } from 'formik';
import { useState } from 'react';
import { boolean, object, string } from 'yup';
import { useCreateEmailContact } from './api/useCreateEmailContact';
import { usePersistHasADog } from '../usePersistHasADog';

export * from './api/useCreateEmailContact';

export interface UseNewsletterParams {
  createdFrom: EmailContactCreatedFromEnum;
  shouldShowToast?: boolean;
  onSubmit?: FormikConfig<PromoNewsletterFormValues>['onSubmit'];
  shouldTriggerGtm?: boolean;
  cachedEmail?: string | null;
  hasADog?: boolean;
}

export const useNewsletter = (params?: UseNewsletterParams) => {
  const {
    createdFrom,
    onSubmit,
    shouldShowToast = true,
    shouldTriggerGtm = true,
    hasADog: initialHasADog,
  } = params ?? {
    shouldShowToast: true,
    shouldTriggerGtm: true,
    createdFrom: EmailContactCreatedFromEnum.NEWSLETTER,
  };

  const { emailMessage, requiredMessage, isValidEmail } = useValidation();
  const [email, setEmail] = useState('');
  const [hasADog, setHasADog] = useState<boolean | undefined>(initialHasADog);

  const { createEmailContactFromForm } = useCreateEmailContact({
    shouldShowToast,
  });
  const { persistHasADog } = usePersistHasADog();

  const newsletterForm: PromoNewsletterData['form'] = {
    validationSchema: object({
      email: string()
        .email(emailMessage)
        .required(requiredMessage)
        .test('is-valid-email', emailMessage, (value) => isValidEmail(value)),

      marketingEmailConsent: boolean().oneOf([true], requiredMessage),
      hasADog: boolean().required(requiredMessage),
    }),
    initialValues: {
      email: '',
      marketingEmailConsent: false,
      createdFrom,
      hasADog: initialHasADog,
    },

    onSubmit: async (values, helpers) => {
      await createEmailContactFromForm(values);
      setEmail(values.email);
      setHasADog(values.hasADog);
      if (shouldTriggerGtm) {
        Analytics.sendGenerateLeadEvent('newsletter');
      }

      await onSubmit?.(values, helpers);

      helpers.resetForm();
      helpers.setFieldValue('email', '');
    },
  };

  return {
    newsletterProps: {
      form: newsletterForm,
    },
    newsletterForm,
    email,
    persistHasADog,
    hasADog,
  };
};
